/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 * See the LICENSE file for the full text.
 */

/**
 * Dialling codes for the checkout's phone field, and what a valid number looks
 * like behind each one.
 *
 * A STATIC LIST, NOT A LOOKUP. This is reference data that changes on the scale
 * of decades, and it is needed before the first paint of a modal somebody is
 * about to pay from — a fetch here would add a spinner to a field, and an
 * outage would block a checkout over a value that has not moved since 1994.
 *
 * SHORT ON PURPOSE. Every entry is a place AthloHive actually takes entries
 * from, or plausibly will. A 200-row list of every territory on earth makes the
 * one people want harder to find, and the field is for reaching somebody about
 * a payment rather than for cataloguing the world.
 *
 * `digits` is what the NATIONAL part must look like, not the whole number —
 * the dial code is chosen separately and is never typed into the number box.
 */

/** India first: it is the default, and the list is ordered by likelihood. */
export const DIAL_CODES = [
  // Indian mobile numbers are exactly ten digits and begin 6-9; the older
  // 2-5 prefixes are landlines, which cannot receive the payment OTP.
  { code: '+91', iso: 'IN', name: 'India', digits: /^[6-9]\d{9}$/, hint: '10 digits, starting 6-9', sample: '9876543210' },
  { code: '+1', iso: 'US', name: 'United States / Canada', digits: /^\d{10}$/, hint: '10 digits', sample: '4155550123' },
  { code: '+44', iso: 'GB', name: 'United Kingdom', digits: /^\d{10}$/, hint: '10 digits', sample: '7700900123' },
  { code: '+61', iso: 'AU', name: 'Australia', digits: /^\d{9}$/, hint: '9 digits', sample: '412345678' },
  { code: '+65', iso: 'SG', name: 'Singapore', digits: /^\d{8}$/, hint: '8 digits', sample: '81234567' },
  { code: '+971', iso: 'AE', name: 'United Arab Emirates', digits: /^\d{9}$/, hint: '9 digits', sample: '501234567' },
  { code: '+94', iso: 'LK', name: 'Sri Lanka', digits: /^\d{9}$/, hint: '9 digits', sample: '711234567' },
  { code: '+977', iso: 'NP', name: 'Nepal', digits: /^\d{10}$/, hint: '10 digits', sample: '9801234567' },
  { code: '+880', iso: 'BD', name: 'Bangladesh', digits: /^\d{10}$/, hint: '10 digits', sample: '1712345678' },
  { code: '+60', iso: 'MY', name: 'Malaysia', digits: /^\d{9,10}$/, hint: '9 or 10 digits', sample: '123456789' },
  { code: '+49', iso: 'DE', name: 'Germany', digits: /^\d{10,11}$/, hint: '10 or 11 digits', sample: '15123456789' },
  { code: '+33', iso: 'FR', name: 'France', digits: /^\d{9}$/, hint: '9 digits', sample: '612345678' },
];

/** What the field starts on when the account has no number of its own. */
export const DEFAULT_DIAL_CODE = '+91';

/** The entry for a dial code, or the default's. Never null, so callers need no
 *  branch for a stored code that has since left the list. */
export function dialCodeEntry(code) {
  return DIAL_CODES.find((c) => c.code === code)
    || DIAL_CODES.find((c) => c.code === DEFAULT_DIAL_CODE);
}

/** Digits only — what the national part actually is, whatever was typed. */
export function normaliseNumber(value) {
  return String(value ?? '').replace(/\D/g, '');
}

/**
 * Is this a number that can receive a payment OTP?
 *
 * Returns {valid, reason}. The reason is what the field says out loud, so it
 * names the specific thing that is wrong rather than "invalid number" — a
 * person who typed nine digits needs to be told it is nine, not that they
 * failed.
 */
export function validateNumber(code, value) {
  const entry = dialCodeEntry(code);
  const digits = normaliseNumber(value);

  if (!digits) return { valid: false, reason: 'Enter your mobile number.' };

  // A leading zero is the trunk prefix people dial domestically. It is never
  // part of the international form, and silently keeping it produces a number
  // one digit too long that looks right to the person who typed it.
  if (digits.startsWith('0')) {
    return { valid: false, reason: 'Leave off the leading 0 — the country code replaces it.' };
  }
  if (!entry.digits.test(digits)) {
    return { valid: false, reason: `${entry.name} numbers are ${entry.hint}.` };
  }
  return { valid: true, reason: null };
}

/** The E.164 form, for anything that wants one string. */
export function toE164(code, value) {
  return `${dialCodeEntry(code).code}${normaliseNumber(value)}`;
}
